import { captureMessage } from '@sentry/browser';
import { useEffect, useRef } from 'react';
import { dissoc } from 'ramda';
import { isNilOrEmpty, isNumber } from 'ramda-adjunct';
import { useHttpCalls } from '../api/useHttpCalls';
import { DEFAULT_SETTINGS } from '../constants/defaultSettings';
import { SMART_SEARCH_KEY } from '../constants/smartSearchKey';
import { useEagGridStore } from '../store/useEagGridStore';
import type { ColumnsSetting, VirtualPreset } from '../types/Api';
import type { QueryIdRequestBody } from '../types/api/data';
import type { EagColumnState } from '../types/EagColumnState';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import type { RowData } from '../types/RowData';
import { buildActivePreset } from '../utils/buildActivePreset';
import { filterTechnicalArray } from '../utils/filterTechnicalArray';
import { getSortModel } from '../utils/getSortModel';
import { getVisibleColumnKeys } from '../utils/getVisibleColumnKeys';
import { isPresetColumnsSettingsInvalid } from '../utils/isPresetColumnsSettingsInvalid';
import { isPresetCustom } from '../utils/isPresetCustom';
import { objectHasEveryEqualPropOfAnother } from '../utils/objectHasEveryEqualPropOfAnother';
import { toIntegerWidth } from '../utils/toIntegerWidth';
import { unwrapFilterValues } from '../utils/unwrapFilterValues';
import { getSearchParamsByGridCode } from '../utils/url/getSearchParamsByGridCode';
import { setSearchParamsByGridCode } from '../utils/url/setSearchParamsByGridCode';
import { EMPTY_SNAPSHOT, isPaginationOnly, updateSnapshot, FetchStateSnapshot } from './fetchStateSnapshot';
import { getCachedPageData, getPageDataCacheKey, setCachedPageData } from './pageDataCache';
const toColumnsSetting = (cs: EagColumnState): ColumnsSetting => ({
  columnKey: cs.key,
  visible: !cs.hidden,
  width: toIntegerWidth(cs.width),
  pinned: cs.pinned ?? null
});
type UseServerDataParams = {
  gridCode: string;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  predefinedColumns: readonly string[];
};
export type UseServerDataResult = {
  fetchPage: (offset: number) => void;
  // rows, totalCount, isLoading, queryId are now in the store
};
export const useServerData = (params: UseServerDataParams): UseServerDataResult => {
  const http = useHttpCalls({
    gridCode: params.gridCode
  });
  const store = useEagGridStore();
  const snapshotRef = useRef<FetchStateSnapshot>(EMPTY_SNAPSHOT);
  const abortControllerRef = useRef<AbortController | null>(null);

  // fetchPage reads all volatile state (filters, sort, settings, activePreset) from
  // store.getState() at call time instead of from closure params.
  //
  // Why no useCallback: fetchPage is never used as a React dependency. Callers read it
  // exclusively via serverDataRef.current (a ref), so identity instability does not
  // cause re-renders or effect re-runs. React Compiler cannot memoize it anyway because
  // `http` (from useHttpCalls) creates a new object each render — but that is harmless.
  //
  // Why no debounce: the previous useDebouncedCallback(fetchInternal, 200ms) caused a
  // stale-closure race — the fetch-trigger effect (EagGridTable line ~571) called
  // serverDataRef.current.fetchPage() while serverDataRef still pointed to the previous
  // render's serverData (ref-sync effect fires after the fetch effect). The 200ms timer
  // then fired with stale sortModel/filters, swallowing the first sort/filter click.
  // React 18 batching handles coalescing of rapid state changes that the debounce
  // previously addressed. Concurrent calls are still protected by abortControllerRef.
  const fetchPage = async (fetchOffset: number) => {
    // Abort any in-flight request before starting a new one
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    store.setIsLoadingWithSnapshot();
    const state = store.getState();
    const columnStates = state.columnStates;
    const gridSettings = state.activePreset?.gridSettings ?? DEFAULT_SETTINGS;
    const filterValues = state.filterValues ?? {};
    const dataQuery = state.dataQuery;
    if (!dataQuery) {
      store.clearPreviousRows();
      store.setIsLoading(false);
      return;
    }
    const activePreset = buildActivePreset(dataQuery.id, columnStates, gridSettings);

    // Compute sortModel from current column states
    const sortModel = getSortModel(columnStates);
    let searchParams = getSearchParamsByGridCode(params.gridCode);
    if (isNilOrEmpty(searchParams)) {
      setSearchParamsByGridCode(params.gridCode, activePreset.dataQueryId);
      searchParams = getSearchParamsByGridCode(params.gridCode);
    }
    const sortBy = sortModel;
    const staticFilters = params.queryModifier ? params.queryModifier({}) : {};
    const modifiedFilters = params.queryModifier ? params.queryModifier(filterValues as QueryFilterObject) : filterValues;
    const limit = gridSettings.itemsPerPage;
    const offset = fetchOffset;
    const rawSmartFilter = (modifiedFilters as Record<string, unknown>)?.[SMART_SEARCH_KEY];
    const smartSearch = rawSmartFilter != null && typeof (rawSmartFilter as Record<string, unknown>)[SMART_SEARCH_KEY] === 'string' ? (rawSmartFilter as Record<string, unknown>)[SMART_SEARCH_KEY] as string : null;
    const filtersWithoutSmartSearch = dissoc(SMART_SEARCH_KEY, modifiedFilters);
    const columns = getVisibleColumnKeys(columnStates, params.predefinedColumns);

    // skipCount: when the queryId hasn't changed (pagination only), the total count
    // is the same — skip the redundant /count request to save a network round-trip.
    const getDataByQueryId = async (resolvedQueryId: string, skipCount: boolean) => {
      store.setQueryId(resolvedQueryId);
      setSearchParamsByGridCode(params.gridCode, resolvedQueryId);
      const cacheKey = getPageDataCacheKey(params.gridCode, resolvedQueryId, offset, limit, columns);
      const cachedData = getCachedPageData(cacheKey);

      // Fetch count and data in parallel. When only paginating (same queryId),
      // skip the count request entirely — it hasn't changed.
      // When page data is cached, skip the network request entirely.
      const [rowCountResult, dataResponse] = await Promise.all([skipCount ? Promise.resolve(undefined) : http.getCountByQuery(resolvedQueryId, controller.signal), cachedData ?? http.getDataByQuery(resolvedQueryId, offset, limit, !!smartSearch, columns, controller.signal)]);

      // A newer fetchPage call aborted this request — don't overwrite the store
      // with stale/empty results from the cancelled request.
      if (controller.signal.aborted) {
        return;
      }
      if (isNumber(rowCountResult)) {
        store.setTotalCount(rowCountResult);
      }
      if (!dataResponse) {
        store.clearPreviousRows();
        store.setIsLoading(false);
        return;
      }
      if (!cachedData) {
        setCachedPageData(cacheKey, dataResponse);
      }
      try {
        const rowData = dataResponse.map(response => ({
          ...response.cells,
          actions: response.actions,
          id: response.id,
          rowNumber: String(response.rowNumber)
        })) as RowData[];
        store.setRows(rowData);
        store.clearPreviousRows();
      } catch (error) {
        if (error instanceof Error) {
          console.error(error.message);
        }
      }
      store.clearPreviousRows();
      store.setIsLoading(false);
    };
    const stateForSnapshot = {
      filterValues: state.filterValues,
      dataQuery,
      columnStates,
      gridSettings: state.activePreset?.gridSettings
    };

    // Fast path: pagination only (offset changed, everything else identical).
    // Skips the expensive JSON.stringify comparison when only the page changed.
    if (isPaginationOnly(snapshotRef.current, stateForSnapshot)) {
      const resolvedQueryId = searchParams?.queryId ?? activePreset.dataQueryId;
      await getDataByQueryId(resolvedQueryId, true);
      snapshotRef.current = updateSnapshot(snapshotRef.current, stateForSnapshot, {
        queryId: resolvedQueryId
      });
      return;
    }
    const comparison = {
      smartSearch,
      filters: filtersWithoutSmartSearch,
      sortBy
    };
    const activePresetJson = JSON.stringify(activePreset);
    const comparisonJson = JSON.stringify(comparison);
    const didPresetChange = snapshotRef.current.presetJson !== null ? snapshotRef.current.presetJson !== activePresetJson : true;
    const isFilterModified = snapshotRef.current.comparisonJson === null ? true : snapshotRef.current.comparisonJson !== comparisonJson;

    // Compare staticFilters against the filters actually stored in the existing data-query on the
    // server. This mirrors the old DataGrid's `objectHasEveryEqualPropOfAnother(staticFilters,
    // props.dataQuery.filters)` check.
    //
    // Why not EMPTY_DATA_QUERY_FILTERS ({}): comparing against {} always returns false when
    // staticFilters is non-empty, which caused grids with a queryModifier (mandatory filters like
    // serviceJobId) to skip the createDataQuery call on first render — using the stale queryId
    // that lacked the mandatory filters → 500 from the backend.
    //
    // dataQuery.filters may be an empty array [] (server returns [] for queries with no filters)
    // or a Record. unwrapFilterValues handles both: Object.entries([]) yields nothing → {}.
    const existingQueryFiltersFlat = unwrapFilterValues(dataQuery.filters as Record<string, unknown>);
    const isAllStaticFilterPresent = objectHasEveryEqualPropOfAnother(staticFilters, existingQueryFiltersFlat);

    // When the preset changed (includes first render), reuse the preset's dataQueryId directly —
    // UNLESS there are mandatory static filters from queryModifier that are missing from the
    // existing data-query. In that case we must create a fresh query with the correct filters.
    if (didPresetChange && isAllStaticFilterPresent) {
      await getDataByQueryId(activePreset.dataQueryId, false);
      snapshotRef.current = updateSnapshot(snapshotRef.current, stateForSnapshot, {
        presetJson: activePresetJson,
        comparisonJson,
        queryId: activePreset.dataQueryId
      });
      return;
    }

    // Filters unchanged and the existing data-query is valid — reuse URL queryId.
    // Skip the /count request when the queryId hasn't changed (pagination only).
    if (!isFilterModified && isAllStaticFilterPresent) {
      const resolvedQueryId = searchParams?.queryId ?? activePreset.dataQueryId;
      const skipCount = snapshotRef.current.queryId === resolvedQueryId;
      await getDataByQueryId(resolvedQueryId, skipCount);
      snapshotRef.current = updateSnapshot(snapshotRef.current, stateForSnapshot, {
        comparisonJson,
        queryId: resolvedQueryId
      });
      return;
    }
    if (controller.signal.aborted) {
      return;
    }
    const queryIdResponse = await http.createDataQuery({
      filters: unwrapFilterValues(filtersWithoutSmartSearch as Record<string, unknown>) as QueryIdRequestBody['filters'],
      order: sortBy,
      smartSearch: smartSearch ?? null
    }, controller.signal);
    if (!queryIdResponse) {
      store.clearPreviousRows();
      store.setIsLoading(false);
      return;
    }
    const newDataQueryId = queryIdResponse.dataQueryId;
    if (!isPresetCustom(activePreset)) {
      const columnsSettings = filterTechnicalArray(columnStates.map(toColumnsSetting));
      const modifiedPreset: VirtualPreset = {
        columnsSettings,
        gridSettings,
        dataQueryId: newDataQueryId
      };
      if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
        captureMessage('updatePreset', scope => scope.setLevel('error').setExtras({
          userDescription: 'update virtual preset from useServerData',
          state: modifiedPreset
        }));
        store.clearPreviousRows();
        store.setIsLoading(false);
        return;
      }
      if (controller.signal.aborted) {
        store.clearPreviousRows();
        store.setIsLoading(false);
        return;
      }
      http.updateVirtualPreset(modifiedPreset).catch(() => {
        // Silent catch — virtual preset sync is best-effort.
        // Server state will be reconciled on next page load.
      });
    }

    // If this request was aborted while in flight, don't update store state
    if (controller.signal.aborted) {
      return;
    }
    await getDataByQueryId(newDataQueryId, false);
    snapshotRef.current = updateSnapshot(snapshotRef.current, stateForSnapshot, {
      presetJson: activePresetJson,
      comparisonJson,
      queryId: newDataQueryId
    });
  };

  // eslint-disable-next-line no-restricted-syntax -- intentional: cleanup-only useEffect with empty deps; useOnMount does not support cleanup returns; aborts any in-flight request when the component unmounts
  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);
  return {
    fetchPage
  };
};